import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UtilityScenarioCaseCard } from '../../components/utility_planning/UtilityScenarioCaseCard';
import { FaSearch, FaPlus, FaFilter } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/utilityApiConfig';

function UtilityScenarioCaseListPage() {
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('desc');

    const navigate = useNavigate();

    useEffect(() => {
        const run = async () => {
            setLoading(true);
            setError('');
            try {
                const token = localStorage.getItem('authToken');
                const response = await axios.get(API_ENDPOINTS.mdhdAnalysisCases, {
                    headers: token ? { Authorization: `Token ${token}` } : {},
                });
                setCases(Array.isArray(response.data) ? response.data : []);
            } catch (err) {
                setError(err.response?.data?.error || err.message || 'Failed to load saved MDHD analyses.');
            } finally {
                setLoading(false);
            }
        };
        run();
    }, []);

    const filteredCases = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        const next = [...cases].filter((caseItem) => {
            if (!query) return true;
            return (
                String(caseItem.case_name || '').toLowerCase().includes(query) ||
                String(caseItem.utility_name || '').toLowerCase().includes(query) ||
                String(caseItem.forecast_scenario || '').toLowerCase().includes(query)
            );
        });

        next.sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'load':
                    comparison = Number(a.total_load_mwh || 0) - Number(b.total_load_mwh || 0);
                    break;
                case 'name':
                    comparison = String(a.case_name || '').localeCompare(String(b.case_name || ''));
                    break;
                case 'utility':
                    comparison = String(a.utility_name || '').localeCompare(String(b.utility_name || ''));
                    break;
                default:
                    comparison = String(a.case_name || '').localeCompare(String(b.case_name || ''));
                    break;
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return next;
    }, [cases, searchQuery, sortBy, sortOrder]);

    const handleDeleteCase = async (caseItem) => {
        if (!caseItem?.id) return;
        const confirmed = window.confirm(`Delete saved case "${caseItem.utility_name || caseItem.case_name || 'selected case'}"?`);
        if (!confirmed) return;
        setDeletingId(caseItem.id);
        try {
            const token = localStorage.getItem('authToken');
            await axios.delete(`${API_ENDPOINTS.mdhdAnalysisCases}${caseItem.id}/`, {
                headers: token ? { Authorization: `Token ${token}` } : {},
            });
            setCases((prev) => prev.filter((item) => item.id !== caseItem.id));
        } catch (err) {
            window.alert(err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to delete saved case.');
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-ui">Loading saved analyses...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
                <div className="text-center bg-white p-12 rounded-lg shadow-xl max-w-lg">
                    <h2 className="text-3xl font-bold text-gray-800 mb-4 font-ui">Unable to Load Cases</h2>
                    <p className="text-gray-600 mb-8 font-ui">{error}</p>
                    <button
                        className="bg-blue-600 hover:bg-blue-700 text-white font-ui font-bold py-3 px-6 rounded-md transition duration-300"
                        onClick={() => navigate('/utility/workflow')}
                    >
                        Back to Workflow
                    </button>
                </div>
            </div>
        );
    }

    if (cases.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
                <div className="text-center bg-white p-12 rounded-lg shadow-xl max-w-lg">
                    <h2 className="text-3xl font-bold text-gray-800 mb-4 font-ui">No Saved MDHD Analyses</h2>
                    <p className="text-gray-600 mb-8 font-ui">
                        Run an MDHD analysis to save multi-year H3 load forecasts to the case library.
                    </p>
                    <button
                        className="bg-blue-600 hover:bg-blue-700 text-white font-ui font-bold py-3 px-6 rounded-md transition duration-300"
                        onClick={() => navigate('/utility/workflow')}
                    >
                        Run Analysis
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800 font-ui">Case Library</h1>
                <button
                    className="bg-blue-600 hover:bg-blue-700 text-white font-ui font-bold py-2 px-4 rounded-md transition duration-300 flex items-center gap-2"
                    onClick={() => navigate('/utility/workflow')}
                >
                    <FaPlus /> New Analysis
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by case name, utility, or scenario..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-ui"
                        />
                    </div>
                    <div className="flex gap-2">
                        <div className="flex items-center gap-2">
                            <FaFilter className="text-gray-400" />
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-ui"
                            >
                                <option value="load">Sort by Load</option>
                                <option value="name">Sort by Name</option>
                                <option value="utility">Sort by Utility</option>
                            </select>
                        </div>
                        <button
                            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                            className="border border-gray-300 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors font-ui"
                            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                        >
                            {sortOrder === 'asc' ? '↑' : '↓'}
                        </button>
                    </div>
                </div>
            </div>

            {filteredCases.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-12 text-center">
                    <p className="text-gray-600 font-ui">No saved analyses match your search.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredCases.map((caseItem) => (
                        <UtilityScenarioCaseCard
                            key={caseItem.id}
                            caseData={caseItem}
                            onDelete={deletingId ? undefined : handleDeleteCase}
                        />
                    ))}
                </div>
            )}

            <div className="mt-6 text-sm text-gray-600 font-ui">
                Showing {filteredCases.length} of {cases.length} saved {cases.length === 1 ? 'analysis' : 'analyses'}
            </div>
        </div>
    );
}

export default UtilityScenarioCaseListPage;
